
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "@/components/theme/theme-provider";
import { Toaster } from "sonner";
import { AuthProvider } from "@/components/auth/AuthProvider";
import { Suspense, lazy, useEffect, useState } from "react";
import { SearchProvider } from "@/components/search/SearchProvider";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { GlobalProgressProvider } from "@/components/progress/GlobalProgressProvider";

// Eagerly loaded pages for critical routes
import Auth from "@/pages/Auth";
import Index from "@/pages/Index"; 
import { RequireAuth } from "@/components/auth/RequireAuth";
import { LoadingState } from "@/components/admin/LoadingState";

// Lazily loaded pages for non-critical routes
const Products = lazy(() => import("@/pages/Products"));
const Settings = lazy(() => import("@/pages/Settings"));
const TrainingData = lazy(() => import("@/pages/TrainingData"));
const Translations = lazy(() => import("@/pages/Translations"));
const TranslationDetails = lazy(() => import("@/pages/TranslationDetails"));
const GeneratedContent = lazy(() => import("@/pages/GeneratedContent"));
const SearchResults = lazy(() => import("@/pages/SearchResults"));
const Tonality = lazy(() => import("@/pages/Tonality"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));

// Optimized query client configuration for better performance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: any) => {
        // Don't retry RLS errors, auth errors, or permission errors
        if (error?.message?.includes('row-level security') || 
            error?.message?.includes('JWT') ||
            error?.message?.includes('permission denied') ||
            error?.status === 403) {
          return false;
        }
        return failureCount < 1; // Reduce retries for faster failure feedback
      },
      staleTime: 1000 * 60 * 10, // 10 minutes - longer caching for stable data
      gcTime: 1000 * 60 * 15, // 15 minutes garbage collection
      refetchOnWindowFocus: false, // Prevent unnecessary refetches
      refetchOnMount: 'always', // Always refetch on mount for fresh data
      refetchOnReconnect: true, // Refetch when network reconnects
      networkMode: 'online', // Only run queries when online
      meta: {
        errorHandler: (error: Error) => {
          // Reduced logging for performance
          if (!error.message.includes('row-level security') && 
              !error.message.includes('JWT') &&
              import.meta.env.DEV) {
            console.warn('Query error:', error.message);
          }
        }
      }
    },
    mutations: {
      retry: 1, // Limit mutation retries
      networkMode: 'online',
    }
  },
});

// Enhanced error fallback with recovery options
const ErrorFallback = () => {
  const [isRecovering, setIsRecovering] = useState(false);
  
  const handleRecovery = async () => {
    setIsRecovering(true);
    try {
      // Clear any cached data that might be causing issues
      queryClient.clear();
      localStorage.removeItem('enableAuthDebug');
      sessionStorage.clear();
      
      // Wait a moment then reload
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (e) {
      console.error('Recovery failed:', e);
      window.location.reload();
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <div className="p-6 max-w-md mx-auto bg-card rounded-lg shadow-lg text-center">
        <h2 className="text-xl font-bold mb-4">Application Error</h2>
        <p className="text-muted-foreground mb-4">
          {import.meta.env.DEV 
            ? "An error occurred during development. Check the console for details."
            : "Sorry, something went wrong. We're working to fix this."}
        </p>
        <div className="space-y-2">
          <button 
            onClick={handleRecovery}
            disabled={isRecovering}
            className="w-full bg-primary text-primary-foreground px-4 py-2 rounded hover:bg-primary/90 disabled:opacity-50"
          >
            {isRecovering ? 'Recovering...' : 'Try Recovery'}
          </button>
          <button 
            onClick={() => window.location.reload()}
            className="w-full bg-secondary text-secondary-foreground px-4 py-2 rounded hover:bg-secondary/90"
          >
            Simple Refresh
          </button>
        </div>
      </div>
    </div>
  );
};

function App() {
  const [initError, setInitError] = useState<string | null>(null);

  // Enhanced environment logging for debugging
  useEffect(() => {
    try {
      console.log("App initialized with env:", {
        mode: import.meta.env.MODE,
        base: import.meta.env.BASE_URL,
        supabaseUrl: import.meta.env.VITE_SUPABASE_URL ? 'Defined' : 'Undefined',
        supabaseKey: import.meta.env.VITE_SUPABASE_ANON_KEY ? 'Defined' : 'Undefined'
      });

      // Check for required environment variables
      if (!import.meta.env.VITE_SUPABASE_URL || !import.meta.env.VITE_SUPABASE_ANON_KEY) {
        setInitError('Missing required environment variables');
        return;
      }

      // Set global error handlers with better logging
      window.addEventListener('unhandledrejection', (event) => {
        console.error('Unhandled promise rejection:', event.reason);
        // Don't prevent default for RLS errors as they're expected
        if (event.reason?.message?.includes('row-level security')) {
          event.preventDefault();
        }
      });

    } catch (error) {
      console.error('App initialization error:', error);
      setInitError('Failed to initialize application');
    }
  }, []);

  // Show initialization error if present
  if (initError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <div className="p-6 max-w-md mx-auto bg-card rounded-lg shadow-lg text-center">
          <h2 className="text-xl font-bold mb-4 text-destructive">Initialization Error</h2>
          <p className="text-muted-foreground mb-4">{initError}</p>
          <button 
            onClick={() => window.location.reload()}
            className="bg-primary text-primary-foreground px-4 py-2 rounded hover:bg-primary/90"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary fallback={<ErrorFallback />}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
          <BrowserRouter>
            <GlobalProgressProvider>
              <AuthProvider>
                <SearchProvider>
                  <div className="app-container min-h-screen bg-background text-foreground">
                    <Suspense fallback={<LoadingState />}>
                      <Routes>
                        <Route path="/auth" element={<Auth />} />
                        <Route path="/" element={<Index />} />
                        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                        <Route 
                          path="/dashboard" 
                          element={
                            <RequireAuth>
                              <Index />
                            </RequireAuth>
                          } 
                        />
                        <Route 
                          path="/search" 
                          element={
                            <RequireAuth>
                              <SearchResults />
                            </RequireAuth>
                          } 
                        />
                        <Route 
                          path="/collections" 
                          element={
                            <RequireAuth>
                              <Products />
                            </RequireAuth>
                          } 
                        />
                        <Route 
                          path="/collections/:collectionId" 
                          element={
                            <RequireAuth>
                              <GeneratedContent />
                            </RequireAuth>
                          } 
                        />
                        <Route 
                          path="/settings" 
                          element={
                            <RequireAuth>
                              <Settings />
                            </RequireAuth>
                          } 
                        />
                        <Route 
                          path="/training" 
                          element={
                            <RequireAuth superAdminOnly>
                              <TrainingData />
                            </RequireAuth>
                          } 
                        />
                        <Route 
                          path="/tonality" 
                          element={
                            <RequireAuth superAdminOnly>
                              <Tonality />
                            </RequireAuth>
                          } 
                        />
                        <Route 
                          path="/translations" 
                          element={
                            <RequireAuth>
                              <Translations />
                            </RequireAuth>
                          } 
                        />
                        <Route 
                          path="/translations/:translationId" 
                          element={
                            <RequireAuth>
                              <TranslationDetails />
                            </RequireAuth>
                          } 
                        />
                      </Routes>
                    </Suspense>
                    <Toaster position="top-right" expand={true} closeButton={true} />
                  </div>
                </SearchProvider>
              </AuthProvider>
            </GlobalProgressProvider>
          </BrowserRouter>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
